import {
  createContext,
  useContext,
  ReactNode,
  Context,
  useState,
  useEffect,
  lazy,
  Suspense,
} from "react";
import { Spin } from "antd";
import { User } from "../../../types/user";
import { UserRoles } from "../../../enums/UserRoles";

// Lazy so @react-oauth/google (the Login widget) stays out of the funnel's main
// bundle — this provider wraps the whole app but only renders <Login> on /admin
// paths for unauthenticated users. See docs/infrastructure/CLIENT_BUNDLE_ARCHITECTURE.md.
const Login = lazy(() => import("./Login"));

type AuthenticationType = {
  isSignedIn: boolean;
  user: User | null;
};

const AuthenticationContext: Context<AuthenticationType | undefined> =
  createContext<AuthenticationType | undefined>(undefined);

export const useAuthentication = (): AuthenticationType => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error(
      "useAuthentication must be used within a AuthenticationProvider",
    );
  }
  return context;
};

interface QuoteProviderProps {
  children: ReactNode;
}

export const AuthenticationProvider = ({ children }: QuoteProviderProps) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdminPath = window.location.pathname.includes("/admin");

  useEffect(() => {
    if (isAdminPath) {
      fetchUser();
    }
  }, [isAdminPath, isSignedIn]);

  // Listen for the auth-expiry signal dispatched by relayEnvironment when
  // the server returns a 401/403 on an admin GraphQL request (expired session).
  // Clear the user so the component tree renders <Login> instead of the dashboard,
  // without a hard page reload (which would lose unsaved state and feel jarring).
  useEffect(() => {
    const handler = () => {
      setUser(null);
      setIsSignedIn(false);
    };
    window.addEventListener("admin-auth-expired", handler);
    return () => window.removeEventListener("admin-auth-expired", handler);
  }, []);

  const fetchUser = () => {
    fetch(process.env.PUBLIC_URL + "/login", {
      method: "GET",
      credentials: "include",
    })
      .then(async (response) => {
        if (response.ok) {
          const fetchedUser = await response.json();
          setUser(fetchedUser);
        }
        setIsLoading(false);
        // Hide the initial loading screen when authentication check is complete
        (window as any).hideInitialLoading?.();
      })
      .catch((err) => {
        console.error("Failed to fetch user:", err);
        setIsLoading(false);
        // Hide the initial loading screen even if fetch fails
        (window as any).hideInitialLoading?.();
      });
  };

  const onLogin = (data: any) => {
    console.log("Logging in with data:", data);
    fetch(process.env.PUBLIC_URL + "/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        console.log("Login response status:", response.status);
        console.log("Response headers:", {
          "set-cookie": response.headers.get("set-cookie"),
          "content-type": response.headers.get("content-type"),
        });
        console.log(
          "All response header names:",
          Array.from(response.headers.keys()),
        );

        if (response.ok) {
          setIsSignedIn(true);
          // Fetch the user after successful login
          setTimeout(() => {
            fetchUser();
          }, 100);
        } else {
          console.error("Login failed with status:", response.status);
        }
      })
      .catch((err) => console.error("Login error:", err));
  };

  const loginStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    position: "relative" as const,
    zIndex: 10000, // Higher than initial-loading (9999)
  };

  // Hide the initial loading screen on admin pages immediately
  useEffect(() => {
    if (isAdminPath) {
      (window as any).hideInitialLoading?.();
    }
  }, [isAdminPath]);

  return (
    <AuthenticationContext.Provider value={{ isSignedIn, user }}>
      {isAdminPath &&
      !user?.roles?.some(
        (r) =>
          r === UserRoles.ADMIN ||
          r === UserRoles.COORDINATOR ||
          r === UserRoles.MARKETING ||
          r === UserRoles.SURGEON ||
          // PREOP (2026-08-27, spec D13) — the pre-op nurse. Without this the
          // role is inert: the server admits her, this gate does not, and she
          // sees the login wall on every /admin URL.
          r === UserRoles.PREOP ||
          // PATIENT_EXPERIENCE (2026-09-11) — coordinator menu/access parity.
          // Same reasoning as PREOP above: without this the role is inert.
          r === UserRoles.PATIENT_EXPERIENCE ||
          // POSTOP (2026-09-24, owner decision D9a) — the post-op nurse. The
          // server admitted it (ADMIN_SIDE_ROLES) and MainMenu got its menu,
          // but this wall never did: a POSTOP-only session minted a cookie and
          // then re-rendered <Login> on every /admin URL (clinical@ lockout,
          // 2026-09-30). Same reasoning as PREOP above.
          r === UserRoles.POSTOP ||
          // OR (2026-10-05, OR Board owner decision D5) — operating-room
          // staff. Added with the role itself, so the clinical@ lockout
          // above never repeats for it. Same reasoning as PREOP above.
          r === UserRoles.OR ||
          // ACCOUNTANT (2026-09-19, external-accounts design Phase 4) — an
          // external (non-org) principal. Same reasoning as PREOP above:
          // without this the role is inert and every CPA login dead-ends on
          // this wall before ever reaching MainMenu's isAccountantOnly gate.
          r === UserRoles.ACCOUNTANT,
      ) ? (
        <div style={loginStyle}>
          <div>
            This section requires admin, coordinator, pre-op, post-op, OR,
            marketing, patient experience, accountant, or surgeon
            privileges.
          </div>
          <Suspense fallback={<Spin size="large" />}>
            <Login onLogin={onLogin} />
          </Suspense>
        </div>
      ) : (
        children
      )}
    </AuthenticationContext.Provider>
  );
};
